import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  WritableSignal,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core'
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms'

import {
  FilterChangedEvent,
  GridApi,
  GridOptions,
  PaginationChangedEvent,
  RowClickedEvent,
  RowDataUpdatedEvent,
  SelectionChangedEvent,
  SortChangedEvent,
} from 'ag-grid-community'

import { Subject, Subscription, debounceTime, merge, switchMap, takeUntil, tap } from 'rxjs'
import { filter } from 'rxjs/operators'

import { FlxThemeModule } from '@flowx/angular-theme'
import {
  FlxIconModule,
  FlxInputModule,
  FlxModalModule,
  FlxModalService,
  FlxSpinnerComponent,
} from '@flowx/angular-ui-toolkit'
import {
  DEFAULT_LOCALE,
  TaskManagerEnumeration,
  TaskManagerEnumerations,
  TemplateConfigInterface,
  View,
  ViewTask,
  ViewTasksFilter,
  ViewTasksSort,
  localizeString,
  resetTaskManagerStores,
  setAccessToken,
  taskManagerAnalytics,
} from '@flowx/core-sdk'

import * as locales from '../../../components/table-wrapper/locales'
import { PublicTasksService } from '../../services/public-tasks.service'
import { TasksService } from '../../services/tasks.service'
import { ViewTasksStateService } from '../../services/view-tasks-state.service'
import { TaskManagerInputs } from '../../task.constants'
import { GET_COLUMN_DEFS, MAP_TASKS_FOR_GRID } from '../../task.helpers'
import { FlxBulkTasksComponent } from '../flx-bulk-tasks/flx-bulk-tasks.component'
import { FlxTaskDetailsComponent } from '../flx-task-details/flx-task-details.component'
import { FlxTasksGridComponent } from '../flx-tasks-grid/flx-tasks-grid.component'

@Component({
  selector: 'flx-task-management',
  templateUrl: './flx-task-management.component.html',
  styleUrls: ['./flx-task-management.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    FlxTasksGridComponent,
    FlxSpinnerComponent,
    FlxInputModule,
    FlxIconModule,
    FlxBulkTasksComponent,
    FlxThemeModule,
    FlxModalModule,
  ],
  providers: [ViewTasksStateService, PublicTasksService, TasksService],
  host: {
    '[style.height]': 'height()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxTasksManagementComponent implements OnInit, OnDestroy {
  #stateService = inject(ViewTasksStateService)
  #tasksService = inject(TasksService)
  #publicTaskService = inject(PublicTasksService)
  #flxModalService = inject(FlxModalService)
  #destroy$ = new Subject<void>()
  #setInitialCalls$ = new Subject<void>()
  #analytics = taskManagerAnalytics(() => this.templateConfig())

  authToken = input.required<string>()
  apiUrl = input.required<string>()
  language = input<string>('en')
  locale = input<string>(DEFAULT_LOCALE)
  view = input<View>()
  viewDefinitionId = input<string>()
  appId = input.required<string>()
  appVersionId = input<string>()
  themeId = input<string>()
  buildId = input<string>()
  workspaceId = input<string>()
  staticAssetsPath = input<string>()
  stages = input<TaskManagerEnumeration[]>([])
  cache = input<boolean>(true)
  height = input<string>()
  templateConfig = input<TemplateConfigInterface>()

  initialized = signal(false)

  initialCallInputs = computed<TaskManagerInputs>(() => ({
    initConfig: {
      authToken: this.authToken(),
      apiUrl: this.apiUrl(),
      appInfo: {
        appId: this.appId(),
        appVersionId: this.appVersionId(),
      },
      language: this.language(),
      themeId: this.themeId(),
      locale: this.locale(),
      staticAssetsPath: this.staticAssetsPath() || '',
      viewId: this.viewDefinitionId(),
      workspaceId: this.workspaceId(),
      buildId: this.buildId() || '',
      stages: this.stages(),
      cache: this.cache(),
    },

    view: this.view(),
  }))

  keywordForm: FormGroup<{ keyword: FormControl<string> }> = new FormGroup({
    keyword: new FormControl(''),
  })

  gridOptions: GridOptions
  gridApi: GridApi
  sorts: ViewTasksSort[] = []
  filters: ViewTasksFilter[] = []
  gridTasks: ViewTask[] = []

  selectedTasks: WritableSignal<ViewTask[]> = signal([])
  rowSelectionEnabled = signal<boolean>(true)
  showBackgroundLoader = signal<boolean>(false)
  taskSubscriber: Subscription
  updatedPriority = output<string>()
  updatedBulk = output<string>()
  searchLabel = computed(() => `${localizeString('@@sys_tm_search')}...`)
  isAutoHeight = computed(() => !this.height() || this.height() === 'auto')

  constructor() {
    this.#stateService.analytics = this.#analytics
    effect(() => {
      const initialCallInputs = this.initialCallInputs()
      // any input change should reset the grid
      if (this.taskSubscriber) {
        this.resetAll()
      }

      this.setInitialCallsAndSubscribe(initialCallInputs)
    })

    effect(() => {
      const domLayout = this.isAutoHeight() ? ('autoHeight' as const) : ('normal' as const)
      this.gridApi?.setGridOption('domLayout', domLayout)
    })

    effect(() => {
      const authToken = this.authToken()
      // Updates the global config and re-authenticates live SSE connections.
      setAccessToken(authToken)
    })
  }

  ngOnInit(): void {
    this.keywordForm.valueChanges
      .pipe(
        filter(() => this.initialized()),
        debounceTime(300),
        takeUntil(this.#destroy$)
      )
      .subscribe((value) => {
        // before the search, which sends the grid back to the first page
        this.#analytics.searched(value.keyword)
        this.#stateService.search(this.gridApi, value.keyword)
      })
  }

  openTaskModal(task: ViewTask): void {
    this.#stateService.setSelectedTask(task)
    this.resetSelected()
    const modalRef = this.#flxModalService.show(
      FlxTaskDetailsComponent,
      {
        closeOnBackdropClick: false,
        backdropClass: 'overlay-backdrop cdk-overlay-dark-backdrop',
      },
      {
        task,
        taskService: this.#stateService,
        priorityUpdateEmitter: this.updatedPriority,
      }
    )

    modalRef.data$.pipe(takeUntil(this.#destroy$)).subscribe(() => {
      this.#stateService.setSelectedTask(null)
    })
  }

  setInitialCallsAndSubscribe(initialCallInputs: TaskManagerInputs): void {
    if (initialCallInputs.view) {
      this.#tasksService.setTasksApiPath(
        `task/api/views/${initialCallInputs.view.resourceId}/wks/${initialCallInputs.initConfig.workspaceId}/app/${initialCallInputs.initConfig.appInfo.appId}/tasks`
      )

      this.#tasksService
        .initService(initialCallInputs.initConfig)
        .pipe(
          tap(() => {
            this.#publicTaskService.setView(initialCallInputs.view, true)
          }),
          switchMap(() =>
            this.#tasksService.fetchEnumerationsAndSetFilters(initialCallInputs.view).pipe(
              tap(() => {
                this.#stateService.initSse()
                this.setRowData()
              }),
              switchMap(() => this.#stateService.getViewTasks(0))
            )
          ),
          takeUntil(merge(this.#destroy$, this.#setInitialCalls$))
        )
        .subscribe(() => {
          this.#setInitialCalls$.next()
        })
    } else {
      this.#publicTaskService
        .getTaskManagerResources(initialCallInputs.initConfig)
        .pipe(
          switchMap(() => {
            this.#stateService.initSse()
            this.setRowData()
            return this.#stateService.getViewTasks(0)
          }),
          takeUntil(merge(this.#destroy$, this.#setInitialCalls$))
        )
        .subscribe(() => {
          this.#setInitialCalls$.next()
        })
    }
  }

  setGridOptions(
    view: View,
    initialTasks: Partial<ViewTask>[],
    enumerations: TaskManagerEnumerations
  ): GridOptions {
    this.rowSelectionEnabled.set(view?.rowSelectionEnabled ?? true)

    return {
      localeText: locales[this.language()],
      ...(this.isAutoHeight() ? { domLayout: 'autoHeight' as const } : {}),
      columnDefs: GET_COLUMN_DEFS(view, enumerations),
      rowData: initialTasks,
      onPaginationChanged: (event: PaginationChangedEvent) => {
        // the page size check comes first: a page size change sends the grid to the first page
        if (event.newPageSize) {
          this.#analytics.pageSizeChanged()
        } else if (event.newPage) {
          this.#analytics.paged(event.api.paginationGetCurrentPage())
        }

        if (event.newPage) {
          this.#stateService.setCurrentPage(event.api.paginationGetCurrentPage())
          if (!this.#stateService.loadedPages.includes(event.api.paginationGetCurrentPage())) {
            this.#stateService.getViewTasks(event.api.paginationGetCurrentPage()).subscribe()
          }
        }

        if (event.newPageSize) {
          this.#stateService.onPageSizeChanged(event)
        }
      },
      onSortChanged: (event: SortChangedEvent) => {
        this.#stateService.onSortChanged(event)
      },
      onFilterChanged: (event: FilterChangedEvent) => {
        this.#stateService.onFiltersChanged(event)
      },
      getRowId: (params) => params.data.agGridId,
      onRowClicked: (event: RowClickedEvent) => {
        const task = this.gridTasks[event.rowIndex]
        this.#analytics.taskOpened(task?.processInstance?.processState)
        this.openTaskModal(task)
      },
      onSelectionChanged: (event: SelectionChangedEvent) => {
        this.selectedTasks.set(
          event.api
            .getSelectedRows()
            .map((row) => this.gridTasks.find((task) => task.tokenUuid === row.agGridId))
        )
        this.#stateService.setKillUpdateTasks(!!this.selectedTasks().length)
      },
      onRowDataUpdated: (event: RowDataUpdatedEvent<any>) => {
        if (this.view()) {
          event.api.forEachNode((rowNode) => {
            const rowElement = document.querySelector(`div[row-id='${rowNode.id}']`)
            if (rowElement) {
              rowElement.setAttribute('data-testId', rowNode.id)
            }
          })
        }
      },
      ...(this.rowSelectionEnabled() ? {} : { rowSelection: null }),
    }
  }

  setRowData(): void {
    this.taskSubscriber = this.#stateService.gridTasks
      .pipe(takeUntil(this.#destroy$))
      .subscribe((tasks: Array<ViewTask>) => {
        this.gridTasks = tasks
        if (!this.initialized()) {
          this.gridOptions = this.setGridOptions(
            this.#publicTaskService.getView(),
            MAP_TASKS_FOR_GRID(tasks),
            this.#publicTaskService.getEnumerations()
          )
          this.initialized.set(true)
          this.#analytics.opened()
        } else if (this.gridApi) {
          this.gridApi.setGridOption('rowData', MAP_TASKS_FOR_GRID(tasks))
          this.gridApi.refreshCells({ force: true })
        }
      })
  }

  setGridApi(api: GridApi): void {
    this.gridApi = api
  }

  resetAll(): void {
    this.taskSubscriber.unsubscribe()
    this.#stateService.setKillUpdateTasks(false)
    this.#stateService.resetTasks()
    this.initialized.set(false)
    this.#analytics.gridReset()
  }

  emitBulkAndReset(bulkUpdateMessage: string): void {
    this.showBackgroundLoader.set(false)
    this.#stateService.initSse()
    this.updatedBulk.emit(bulkUpdateMessage)
    if (bulkUpdateMessage === 'success') {
      this.resetSelected()
    }
  }

  resetSelected(): void {
    this.selectedTasks.set([])
    this.#stateService.setKillUpdateTasks(false)
    this.gridApi.deselectAll()
  }

  onBulkStarted(): void {
    this.showBackgroundLoader.set(true)
    this.#stateService.closeSseConnection()
  }

  ngOnDestroy(): void {
    this.#destroy$.next()
    this.#destroy$.complete()
    this.#setInitialCalls$.complete()
    this.#stateService.closeSseConnection()
    resetTaskManagerStores()
  }
}
